import type { MenschMove, MenschView } from "@bing/engine-mensch";
import type { ReactNode } from "react";
import { getMenschPlayersOnline } from "../settings/prefs";
import { fa } from "../i18n/fa";
import { MenschTable, type MenschIdentity } from "../screens/MenschTable";
import { useSession } from "../store/context";
import { MenschEndOverlay } from "./MenschGame";
import type { LobbyPlayer } from "./online-connection";
import { OnlineGame } from "./OnlineGame";

/** Online منچ — a thin wrapper over the shared `OnlineGame` shell (public quick-match OR a host's
 *  private table). 4 seats, perfect information: no CardDeck, no team overlay. Threads each seat's
 *  identity through so a real opponent shows their own name and avatar on their strip. */
/** The roster onto seat-indexed identities the board renders directly — a bot or an empty seat
 *  null. ONE mapping for the board and its end card (card-98 review E-M1). */
function menschIdentities(
  players: readonly LobbyPlayer[] | undefined,
  seats: number,
): (MenschIdentity | null)[] {
  const bySeat: (MenschIdentity | null)[] = Array.from({ length: seats }, () => null);
  for (const p of players ?? []) {
    if (p.seat < 0 || p.seat >= bySeat.length) continue;
    bySeat[p.seat] = p.isBot
      ? null
      : {
          nickname: p.name,
          avatarId: p.user?.avatarId,
          avatarPhotoId: p.user?.avatarPhotoId,
          isOfficial: p.user?.isOfficial,
        };
  }
  return bySeat;
}

export function MenschOnlineGame(): ReactNode {
  const profile = useSession((s) => s.profile);
  /*
   * PUBLIC is FOUR SEATS; the HOST's table is whatever size they picked.
   *
   * A public table offers no choices (2026-08-26): the seat-count step is gone from the lobby and
   * every quick-match منچ table is the standard four-handed one. A PRIVATE table still comes in
   * three sizes, and each size is its own room type — `maxClients` is fixed at `define()` time —
   * so the private name still carries the host's pick.
   *
   * ⚠️ `privateRoom` MUST BE STATED. `OnlineGame` otherwise derives it by swapping `-online` →
   * `-private`, which on a fixed public name would ask for `mensch-private` for EVERY host and
   * silently collapse the 2- and 3-seat tables into four-handed ones.
   */
  const players = getMenschPlayersOnline();
  const suffix = players === 4 ? "" : String(players);
  return (
    <OnlineGame<MenschMove, MenschView>
      gameId="mensch"
      room="mensch-online"
      privateRoom={`mensch${suffix}-private`}
      title={fa.mensch.title}
      renderTable={({ view, legal, send, leave, players, timer }) => {
        const bySeat = menschIdentities(players, view.tokens.length);
        return (
          <MenschTable
            view={view}
            legal={legal}
            onMove={send}
            onLeave={leave}
            self={profile}
            players={bySeat}
            timer={timer}
          />
        );
      }}
      renderWin={({
        result,
        view,
        again,
        leave,
        players,
        againDisabled,
        againHint,
        againHidden,
      }) => (
        <MenschEndOverlay
          result={result}
          view={view}
          // card-98 item 12: the rows name the players — the SAME seat-indexed identities the board gets
          self={profile}
          players={menschIdentities(players, view.tokens.length)}
          onAgain={again}
          againDisabled={againDisabled}
          againHint={againHint}
          againHidden={againHidden}
          onLeave={leave}
        />
      )}
    />
  );
}
